import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PROPERTIES_REFERENCES } from '@app/constants';
import { CurrentCognitoUser } from '@app/interface/cognito-user.interface';
import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { ICredentials } from '@aws-amplify/core';
import { environment } from '@environments/environment';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { BehaviorSubject } from 'rxjs';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly cognitoIdService: string = environment.cognitoIdentityService;
  private infoChapter: any;
  public tokenExpirationTime: number = 0;
  private readonly loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();
  userEmail: string | null = null;

  public tokenRefreshTimer: any;
  public tokenRefreshInProgress: boolean = false;

  token: any;

  constructor(
    private readonly http: HttpClient,
    private readonly route: Router,
    private tokenService: TokenService
  ) {
    this.startTokenRefreshTimer();
  }

  signOut(): Promise<any> {
    return Auth.signOut().then(() => localStorage.removeItem('logged'));
  }

  googleSocialSignIn(): Promise<ICredentials> {
    this.loadingSubject.next(true);
    localStorage.setItem('loading', 'true');

    return Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google,
    }).finally(() => {
      this.loadingSubject.next(false);
    });
  }

  get getTnfoChapter(): any {
    return { ...this.infoChapter };
  }

  set setTnfoChapter(value: any) {
    this.infoChapter = value;
  }

  getToken() {
    const token = window.localStorage.getItem(
      `${this.cognitoIdService}.${this.getGoogleUser()}.idToken`
    );
    return token;
  }

  getEmail(): string | null {
    const token = window.localStorage.getItem(
      `${this.cognitoIdService}.${this.getGoogleUser()}.idToken`
    );

    if (!token) {
      return null;
    }

    const userEmail = this.tokenService.getEmailFromToken(token);
    return userEmail;
  }

  isAuth() {
    return this.getToken() !== null && this.loadingSubject.value === false;
  }

  getGoogleUser() {
    return window.localStorage.getItem(`${this.cognitoIdService}.LastAuthUser`);
  }

  getCurrentUser(): Promise<CurrentCognitoUser> {
    return Auth.currentAuthenticatedUser() as Promise<CurrentCognitoUser>;
  }

  refreshToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const currentUser = Auth.currentAuthenticatedUser();

      currentUser
        .then((user: CognitoUser) => {
          const session = user.getSignInUserSession();

          if (session) {
            const refreshToken = session.getRefreshToken();

            if (refreshToken) {
              user.refreshSession(refreshToken, (err: any, refreshedSession: any) => {
                if (err) {
                  reject(err);
                } else {
                  const refreshedToken = refreshedSession.getIdToken().getJwtToken();

                  resolve(refreshedToken);
                }
              });
            } else {
              const error = new Error('No se encontró un refreshToken válido');
              reject(error);
            }
          } else {
            const error = new Error('No se encontró una sesión de usuario');
            reject(error);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  startTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer || this.tokenRefreshInProgress) {
      return;
    }

    this.stopTokenRefreshTimer();

    const refreshTokenInterval = 60 * 60 * 1000;

    this.tokenRefreshTimer = setInterval(() => {
      this.tokenRefreshInProgress = true;

      this.refreshToken()
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          this.tokenRefreshInProgress = false;
        });
    }, refreshTokenInterval);
  }

  stopTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  isSameUser(): boolean {
    const currentRoute = this.route.routerState.snapshot.root;
    const profileEmail = currentRoute.firstChild?.params[PROPERTIES_REFERENCES.id];
    return profileEmail === this.getEmail();
  }
}
